import React from "react";
import "./AddMicrogridModal.css";

export default function AddMicrogridModal({
  open,
  step,
  microgridName,
  setMicrogridName,
  formData,
  errors,
  submitError,
  isSubmitting,
  uploading,
  uploadMessage,
  isEditingLoading,
  onClose,
  handleChange,
  handleFileChange,
  handleCreateMicrogrid,
  handleSaveMeterDetails,
  // Prod-only guard (ahead of the Data Center version): the original primary
  // demand_charge of the TOU-8 meter being edited (null otherwise); a
  // ratcheted one keeps its ratchet fields visible under TOU-8.
  existingPrimaryDemandCharge = null,
}) {
  if (!open) return null;

  if (step === 1) {
    return (
      <div className="popup-overlay">
        <div className="popup">
          <div className="popup-header">
            <h3>New Microgrid</h3>
            <button className="popup-close" onClick={onClose}>
              ✕
            </button>
          </div>
          <p>
            To create a new Simulation, please enter a Microgrid Name that has
            not been previously used.
          </p>
          <div className="form-section">
            <label>
              Microgrid Name<span className="required">*</span>
            </label>
            <input
              type="text"
              value={microgridName}
              onChange={(e) => setMicrogridName(e.target.value)}
              placeholder="Ex. John"
            />
            {submitError && (
              <p style={{ color: "red", fontSize: "0.9em" }}>{submitError}</p>
            )}
          </div>
          <div className="popup-actions">
            <button
              className="create-btn"
              onClick={handleCreateMicrogrid}
              disabled={isSubmitting}
            >
              {isSubmitting ? <div className="button-spinner" /> : "Create"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2 || step === 3) {
    return (
      <div className="popup-overlay">
        <div className="popup">
          <div className="popup-header">
            <h3>{step === 2 ? "Add Meter Details" : "Edit Meter Details"}</h3>
            <button className="popup-close" onClick={onClose}>
              ✕
            </button>
          </div>
          {isEditingLoading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <p>Loading meter details...</p>
              <div className="spinner-customer"></div>
            </div>
          ) : (
            <>
              <p>To {step === 2 ? "create a new" : "edit the"} meter.</p>

              <div className="form-section">
                <label style={{ color: "#333" }}>
                  Meter Name :<span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  maxLength={50}
                  id="meter_Name"
                  value={formData.meter_Name}
                  onChange={handleChange}
                  placeholder="Battery 111"
                />
                {errors.meter_Name && (
                  <span className="add-device-error-message">{errors.meter_Name}</span>
                )}
              </div>

              <div className="form-section">
                <label>Serial Number :</label>
                <input
                  type="text"
                  id="serial_number"
                  maxLength={50}
                  value={formData.serial_number}
                  onChange={handleChange}
                  placeholder="SN8392"
                />
                {errors.serial_number && (
                  <span className="add-device-error-message">{errors.serial_number}</span>
                )}
              </div>

              <div className="form-section">
                <label>Make and Model:</label>
                <input
                  type="text"
                  maxLength={50}
                  id="make_and_model"
                  value={formData.make_and_model}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  placeholder="Battery 111"
                />
                {errors.make_and_model && (
                  <span className="add-device-error-message">{errors.make_and_model}</span>
                )}
              </div>

              <div className="form-section" style={{ marginTop: "16px" }}>
                <label>
                  Utility Tariff Data File (.csv){" "}
                  <span className="required">*</span>
                </label>
                <div className="file-input-container">
                  <div className="file-name-box">
                    <span
                      className={`file-name-text ${
                        formData.csv_file_name ? "file-selected" : "no-file"
                      }`}
                    >
                      {formData.csv_file_name || "No file selected"}
                    </span>
                    {uploading && <div className="upload-spinner" />}
                  </div>
                  <label htmlFor="csv-upload-microgrid" className="add-device-upload-button">
                    Select File
                  </label>
                  <input
                    id="csv-upload-microgrid"
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </div>
                <small>Accepted formats: .csv</small>
                {errors.csv_file_name && (
                  <span className="add-device-error-message">{errors.csv_file_name}</span>
                )}
                {uploadMessage && (
                  <div
                    className={`upload-message ${
                      uploadMessage.type === "success" ? "success" : "error"
                    }`}
                  >
                    {uploadMessage.text}
                  </div>
                )}
                {errors.uploadMessage && (
                  <span className="add-device-error-message">{errors.uploadMessage}</span>
                )}
              </div>

              <div className="form-section">
                <label>
                  Demand Charge: <span className="required-asterisk">*</span>
                </label>
                <select
                  id="demand_charge_strategy"
                  value={formData.demand_charge_strategy}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="none">None</option>
                  <option value="noncoincident">Noncoincident Demand Charge</option>
                  <option value="ratcheted">Ratcheted Demand Charge</option>
                  <option value="tou8">TOU-8 (3-Tier: FRD + Summer/Winter TRD)</option>
                </select>
              </div>

              {formData.demand_charge_strategy === "tou8" && (
                <>
                  <div className="form-section">
                    <label>
                      Facilities-Related Demand (FRD) — all hours, year-round:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="frd_rate"
                        value={formData.frd_rate}
                        onChange={handleChange}
                        placeholder="e.g. 28.68"
                      />
                      <span className="input-group-text">$/kW</span>
                    </div>
                    {errors.frd_rate && (
                      <span className="add-device-error-message">{errors.frd_rate}</span>
                    )}
                  </div>
                  <div className="form-section">
                    <label>
                      Summer On-Peak TRD — Jun–Sep, weekdays 4–9 pm:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="summer_trd_rate"
                        value={formData.summer_trd_rate}
                        onChange={handleChange}
                        placeholder="e.g. 38.51"
                      />
                      <span className="input-group-text">$/kW</span>
                    </div>
                    {errors.summer_trd_rate && (
                      <span className="add-device-error-message">{errors.summer_trd_rate}</span>
                    )}
                  </div>
                  <div className="form-section">
                    <label>
                      Winter Mid-Peak TRD — Oct–May, weekdays 4–9 pm:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="winter_trd_rate"
                        value={formData.winter_trd_rate}
                        onChange={handleChange}
                        placeholder="e.g. 10.34"
                      />
                      <span className="input-group-text">$/kW</span>
                    </div>
                    {errors.winter_trd_rate && (
                      <span className="add-device-error-message">{errors.winter_trd_rate}</span>
                    )}
                  </div>
                  <div className="form-section">
                    <label>
                      Demand Escalator Fraction:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="demand_charge_escalator_fraction"
                        value={formData.demand_charge_escalator_fraction}
                        onChange={handleChange}
                        placeholder="Enter Escalator Fraction"
                      />
                      <span className="input-group-text">%/yr</span>
                    </div>
                    {errors.demand_charge_escalator_fraction && (
                      <span className="add-device-error-message">
                        {errors.demand_charge_escalator_fraction}
                      </span>
                    )}
                  </div>
                </>
              )}

              {(formData.demand_charge_strategy === "noncoincident" ||
                formData.demand_charge_strategy === "ratcheted") && (
                <>
                  <div className="form-section">
                    <label>
                      Demand Charge Rate:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="demand_charge"
                        value={formData.demand_charge}
                        onChange={handleChange}
                        placeholder="Enter Demand Charge Rate"
                      />
                      <span className="input-group-text">$/kW</span>
                    </div>
                    {errors.demand_charge && (
                      <span className="add-device-error-message">{errors.demand_charge}</span>
                    )}
                  </div>
                  <div className="form-section">
                    <label>
                      Demand Escalator Fraction:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="demand_charge_escalator_fraction"
                        value={formData.demand_charge_escalator_fraction}
                        onChange={handleChange}
                        placeholder="Enter Escalator Fraction"
                      />
                      <span className="input-group-text">%/yr</span>
                    </div>
                    {errors.demand_charge_escalator_fraction && (
                      <span className="add-device-error-message">
                        {errors.demand_charge_escalator_fraction}
                      </span>
                    )}
                  </div>
                </>
              )}

              {(formData.demand_charge_strategy === "ratcheted" ||
                (formData.demand_charge_strategy === "tou8" &&
                  existingPrimaryDemandCharge?.type === "ratcheted")) && (
                <>
                  <div className="form-section">
                    <label>
                      Ratchet Percentage:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="ratchet_percentage"
                        value={formData.ratchet_percentage}
                        onChange={handleChange}
                        placeholder="Enter Ratchet Percentage (e.g., 0.80)"
                      />
                      <span className="input-group-text">%</span>
                    </div>
                    {errors.ratchet_percentage && (
                      <span className="add-device-error-message">
                        {errors.ratchet_percentage}
                      </span>
                    )}
                  </div>
                  <div className="form-section">
                    <label>
                      Lookback Months:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="lookback_months"
                        value={formData.lookback_months}
                        onChange={handleChange}
                        placeholder="Enter Lookback Months (e.g., 12)"
                      />
                      <span className="input-group-text">months</span>
                    </div>
                    {errors.lookback_months && (
                      <span className="add-device-error-message">
                        {errors.lookback_months}
                      </span>
                    )}
                  </div>
                  <div className="form-section">
                    <label>
                      Historical Peak:{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="input-group input-group-unified-focus">
                      <input
                        type="text"
                        className="form-control"
                        id="historical_peak_kw"
                        value={formData.historical_peak_kw}
                        onChange={handleChange}
                        placeholder="Enter Historical Peak"
                      />
                      <span className="input-group-text">kW</span>
                    </div>
                    {errors.historical_peak_kw && (
                      <span className="add-device-error-message">
                        {errors.historical_peak_kw}
                      </span>
                    )}
                  </div>
                </>
              )}

              {submitError && <div className="error-message-box">{submitError}</div>}

              <div className="popup-actions-button">
                <button
                  className="create-btn"
                  onClick={handleSaveMeterDetails}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="button-spinner" />
                  ) : step === 2 ? (
                    "Create"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
